import { ui } from "@patient-watch/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { formComponents as fc } from "@/features/i18n/formComponents";
import { useAuthCopy } from "@/features/i18n/authCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { AuthContactTabs } from "@/features/auth/components/layout";
import useSmartAuthMutation from "@/features/auth/hooks/useSmartAuth";
import type { SmartAuthSchemaType } from "@/features/auth/types/smartAuthSchema";
import { smartAuthSchema } from "@/features/auth/types/smartAuthSchema";
import { contactMethodToMethodParam } from "@/features/auth/utils/authMethodSearchParam";
import { useEffect } from "react";

interface Props {
    onSuccess?: () => void;
    initialContactMethod?: "email" | "phone";
    initialEmail?: string;
    initialPhone?: string;
}
const SmartAuthForm = (props: Props) => {
    const copy = useAuthCopy();
    const { locale } = useAppLocale();
    const navigate = useNavigate();
    const location = useLocation();
    const form = useForm<SmartAuthSchemaType>({
        resolver: zodResolver(smartAuthSchema),
        defaultValues: {
            contactMethod: props.initialContactMethod ?? "email",
            email: props.initialEmail ?? "",
            phone: props.initialPhone ?? "",
        },
    });

    useEffect(() => {
        form.reset({
            contactMethod: props.initialContactMethod ?? "email",
            email: props.initialEmail ?? "",
            phone: props.initialPhone ?? "",
        });
    }, [props.initialContactMethod, props.initialEmail, props.initialPhone, form]);

    const contactMethod = form.watch("contactMethod");

    const smartAuthMutation = useSmartAuthMutation();

    const onSubmit: SubmitHandler<SmartAuthSchemaType> = (formData) => {
        smartAuthMutation.mutate(formData, {
            onSuccess: props.onSuccess,
        });
    };

    const setContactMethod = (next: "email" | "phone") => {
        if (next === contactMethod) return;
        form.setValue("contactMethod", next);
        if (next === "phone") {
            form.setValue("email", "");
        } else {
            form.setValue("phone", "");
        }
        form.clearErrors();
        const currentSearch = location.search as Record<string, unknown>;
        void navigate({
            search: { ...currentSearch, method: contactMethodToMethodParam(next) } as never,
            replace: true,
        });
    };

    return (
        <ui.Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                <AuthContactTabs value={contactMethod} onChange={setContactMethod} />
                {contactMethod === "email" ? (
                    <fc.Email autoFocus tabIndex={0} />
                ) : (
                    <fc.Phone autoFocus label={copy.phone} placeholder={copy.enterPhoneNumber} locale={locale} />
                )}
                <fc.NextButton isLoading={smartAuthMutation.isPending} />
            </form>
        </ui.Form>
    );
};

export default SmartAuthForm;
